<template>
  <div>
    <p
      v-if="note"
      class="mb-2 text-gray-500 text-right text-xs"
    >
      {{ note }}
    </p>
    <b-table
      :data="orders"
      :default-sort="[sortField, sortOrder]"
      :loading="loading"
      backend-sorting
      scrollable
      @sort="onSort"
    >
      <b-table-column
        :width="28"
        field="id"
        header-class="font-medium text-xs"
        label="No."
        v-slot="props"
      >
        <span class="text-sm">{{
          props.index + 1 + (currentPage - 1) * currentPerPage
        }}.</span>
      </b-table-column>
      <b-table-column
        :width="44"
        field="action"
        header-class="font-medium text-xs"
        label="訂購ID"
        v-slot="props"
      >
        <span class="text-sm">{{ props.row.id }}</span>
      </b-table-column>
      <b-table-column
        :visible="exceptDraft"
        :width="56"
        field="action"
        header-class="font-medium text-xs"
        label="例行性ID"
        v-slot="props"
      >
        <w-button
          v-if="props.row.schedule?.id && props.row.schedule?.version === 2"
          class="link"
          font-weight-class="font-normal"
          type="text"
          @click="toggleScheduleModel(true, props.row)"
        >
          {{ props.row.schedule?.id }}
        </w-button>
        <template v-else>
          <span class="text-sm">{{ props.row.schedule?.id }}</span>
        </template>
      </b-table-column>
      <b-table-column
        :width="176"
        field="name"
        header-class="font-medium text-xs"
        label="訂購名稱"
        v-slot="props"
      >
        <b-tooltip
          :label="props.row.name"
          append-to-body
          type="is-dark"
        >
          <div class="max-w-[176px] truncate">
            <span class="max-w-[176px] text-sm truncate">{{ props.row.name }}</span>
          </div>
        </b-tooltip>
      </b-table-column>
      <b-table-column
        :width="244"
        field="start"
        header-class="font-medium max-w-[272px] text-xs"
        cell-class="max-w-[272px]"
        label="開放訂購時段"
        sortable
        v-slot="props"
      >
        <w-date-time-display :date-time="props.row" />
      </b-table-column>
      <b-table-column
        :width="276"
        field="receive"
        header-class="font-medium max-w-[304px] text-xs"
        cell-class="max-w-[304px]"
        label="領取時段"
        sortable
        v-slot="props"
      >
        <div class="flex gap-4 items-center">
          <w-date-time-display
            v-if="props.row.receive_times?.length"
            :date-time="props.row.receive_times?.[0]"
          />
          <b-dropdown
            v-if="props.row.receive_times?.length > 1"
            append-to-body
            aria-role="list"
            max-height="200"
            position="is-bottom-left"
            scrollable
          >
            <template #trigger>
              <i class="cursor-pointer icon-down-chevron text-base" />
            </template>
            <b-dropdown-item
              v-for="(receiveTime, index) in extraReceiveTimes(props.row.receive_times)"
              :key="`receiveTime-${props.row.id}-${index}`"
              :focusable="false"
              aria-role="listitem"
              class="w-max"
              custom
            >
              <w-date-time-display :date-time="receiveTime" />
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </b-table-column>
      <b-table-column
        v-if="inDraft"
        :width="128"
        field="update"
        header-class="font-medium text-xs"
        label="最後更新紀錄"
        sortable
        v-slot="props"
      >
        <span class="text-sm mr-1">{{ dayjs(props.row.updated_at).format('YYYY/MM/DD') }}</span>
        <span class="font-medium text-seaweed-green-500 text-sm">
          {{ dayjs(props.row.updated_at).format('HH:mm') }}
        </span>
      </b-table-column>
      <b-table-column
        :visible="inCompleted"
        :width="68"
        centered
        field="expected_member_count"
        header-class="font-medium text-xs"
        label="訂單金額"
        v-slot="props"
      >
        <span class="text-sm">${{
          props.row.report?.ordered_total || 0 | commaFormat
        }}</span>
      </b-table-column>
      <b-table-column
        :visible="exceptCancelled"
        :width="68"
        centered
        field="expected_member_count"
        header-class="font-medium text-xs"
        label="預計總人數"
        v-slot="props"
      >
        <span class="text-sm">{{
          props.row.report?.expected_member_count || 0 | commaFormat
        }}</span>
      </b-table-column>
      <b-table-column
        :visible="inCompleted || inEnded || inOnGoing"
        :width="56"
        centered
        field="ordered_member_count"
        header-class="font-medium text-xs"
        label="已訂人數"
        v-slot="props"
      >
        <span class="text-sm">{{
          props.row.report?.ordered_member_count || 0 | commaFormat
        }}</span>
      </b-table-column>
      <b-table-column
        :visible="inCompleted || inEnded || inOnGoing"
        :width="56"
        centered
        field="unordered_member_count"
        header-class="font-medium text-xs"
        label="未訂人數"
        v-slot="props"
      >
        <span class="text-sm">{{
          props.row.report?.unordered_member_count || 0 | commaFormat
        }}</span>
      </b-table-column>
      <b-table-column
        :visible="inScheduled || inEnded || inOnGoing"
        :width="56"
        centered
        field="limit"
        header-class="font-medium text-xs"
        label="訂購限制"
        v-slot="props"
      >
        <span class="text-sm">{{ getLimitation(props.row.limit) }}</span>
      </b-table-column>
      <b-table-column
        :visible="inEnded || inOnGoing"
        :width="36"
        centered
        field="share"
        header-class="font-medium text-xs"
        label="訪客"
        v-slot="props"
      >
        <a
          v-if="isShareModeOff(props.row) || isShareModeOn(props.row)"
          :href="getShareLink(props.row)"
          class="link"
          target="_blank"
        >
          {{ props.row.report?.ordered_guest_count || 0 | commaFormat }}
        </a>
      </b-table-column>
      <b-table-column
        :label="editItem.label"
        centered
        field="share"
        header-class="font-medium max-w-[48px] min-w-[48px] text-xs w-12"
        v-slot="props"
      >
        <b-tooltip
          :label="editItem.tooltip"
          append-to-body
          type="is-dark"
        >
          <w-button
            class="hover:text-primary-700 text-primary-500"
            type="icon"
            @click="editItem.action(props.row.id)"
          >
            <i :class="editItem.icon" class="text-base" />
          </w-button>
        </b-tooltip>
      </b-table-column>
      <b-table-column
        :visible="inCancelled || inCompleted || inEnded || inOnGoing"
        centered
        field="share"
        header-class="font-medium max-w-[48px] min-w-[48px] text-xs w-12"
        label="統計"
        v-slot="props"
      >
        <b-tooltip
          append-to-body
          label="查看統計"
          type="is-dark"
        >
          <w-button
            class="hover:text-primary-700 text-primary-500"
            type="icon"
            @click="toOrderOverview(props.row.id)"
          >
            <i class="icon-chart text-base" />
          </w-button>
        </b-tooltip>
      </b-table-column>
      <b-table-column
        :visible="inEnded"
        centered
        field="complete"
        header-class="font-medium max-w-[48px] min-w-[48px] text-xs w-12"
        label="完成"
        v-slot="props"
      >
        <b-tooltip
          append-to-body
          label="完成訂購"
          type="is-dark"
        >
          <w-button
            class="hover:text-primary-700 text-primary-500"
            type="icon"
            @click="onOrderFinish(props.row)"
          >
            <i class="icon-finish text-base" />
          </w-button>
        </b-tooltip>
      </b-table-column>
      <b-table-column
        :visible="inDraft"
        centered
        field="delete"
        header-class="font-medium max-w-[48px] min-w-[48px] text-xs w-12"
        label="刪除"
        v-slot="props"
      >
        <b-tooltip
          append-to-body
          label="刪除草稿"
          type="is-dark"
        >
          <w-button
            class="hover:text-red-700 text-red-500"
            type="icon"
            @click="onDraftDelete(props.row)"
          >
            <i class="icon-trash text-base" />
          </w-button>
        </b-tooltip>
      </b-table-column>
      <b-table-column
        :visible="inScheduled || inEnded || inOnGoing"
        :width="24"
        centered
        field="share"
        header-class="font-medium text-xs"
        label=""
        v-slot="props"
      >
        <b-dropdown
          append-to-body
          aria-role="list"
          position="is-bottom-left"
        >
          <template #trigger>
            <b-tooltip
              append-to-body
              label="更多"
              type="is-dark"
            >
              <i class="icon-more-vertical more" />
            </b-tooltip>
          </template>
          <template
            v-for="(extraItem, index) in getExtraItems(props.row)"
          >
            <b-dropdown-item
              v-if="extraItem.show"
              :key="`extraItem-${props.row.id}-${index}`"
              aria-role="listitem"
              @click="extraItem.action()"
            >
              <span :class="extraItem.labelClasses">{{ extraItem.label }}</span>
            </b-dropdown-item>
          </template>
        </b-dropdown>
      </b-table-column>

      <template #empty>
        <p class="my-12 text-center text-gray-700 text-sm">{{ emptyText }}</p>
      </template>
    </b-table>
    <cancel-confirmation
      v-model="openCancelConfirmation"
      :order="actionOrder"
      :loading="isCancelingOrder"
      @confirm="onOrderCancel"
    />
    <copy-link-modal
      v-model="openCopyLinkModal"
      mode="list"
      :order="actionOrder"
      @close="setActionOrder({})"
    />
    <disable-custom-link-modal
      v-model="openDisableCustomLinkModal"
      :order="actionOrder"
      @close="setActionOrder({})"
    />
    <enable-custom-link-modal
      v-model="openEnableCustomLinkModal"
      :order="actionOrder"
      @close="setActionOrder({})"
    />
    <draft-delete-confirmation
      v-model="openConfirmDeleteDraftDialog"
      :loading="isDeletingDraft"
      @confirm="deleteDraft()"
    />
    <receive-notification-modal
      v-if="openNotifyModal"
      v-model="openNotifyModal"
      :order="actionOrder"
      @close="setActionOrder({})"
    />
    <schedule-list-modal
      v-model="openScheduleListModal"
      :order="actionOrder"
      @close="openScheduleListModal = false"
    />
    <schedule-edit-modal
      v-if="openScheduleEditModal"
      v-model="openScheduleEditModal"
      :order="actionOrder"
      @close="openScheduleEditModal = false"
    />
  </div>
</template>

<script>
import OrderList from './OrderList';

export default OrderList;
</script>

<style lang="scss" scoped>
@import 'OrderList';
</style>
